import React, { Component } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import API from "../utils/api";
import { sanitizePhone } from "../utils/phone";
import { loadCallStatusLabels, getCallStatusLabel } from "../utils/callStatusCache";
import { hasPermission } from "../utils/permissions";
import { PERMISSION } from "../utils/constants";

const STATUS_MAP = {
    2: { label: "Spam", cls: "label-danger" },
    5: { label: "New-Incomplete", cls: "label-warning" },
    6: { label: "New-Complete", cls: "label-success" },
    7: { label: "Old-Incomplete", cls: "label-warning" },
    8: { label: "Old-Complete", cls: "label-info" },
    9: { label: "From Tele", cls: "label-primary" },
};

const FILTERS_KEY = "teleLeadFilters";

// Helper to load saved filters from localStorage
const loadFilters = () => {
    try {
        const saved = localStorage.getItem(FILTERS_KEY);
        if (saved) return JSON.parse(saved);
    } catch (e) { }
    return { leadId: "", keyword: "", from: "", to: "", source: "", phone: "", workedBy: "", callStatus: "", campaign: "", team: "", page: 0 };
};

class LeadsPage extends Component {
    constructor(props) {
        super(props);
        const q = loadFilters();
        this.state = {
            leads: [],
            count: 0,
            loading: true,
            page: q.page,
            pageSize: 25,
            leadId: q.leadId || "",
            keyword: q.keyword,
            from: q.from,
            to: q.to,
            source: q.source,
            phone: q.phone || "",
            workedBy: q.workedBy || "",
            callStatus: q.callStatus || "",
            campaign: q.campaign || "",
            team: q.team || "",
            campaignOptions: [],
            sourceOptions: [],
            executiveOptions: [],
            teamOptions: [],
            callStatuses: [],
            callStatusLabels: {},
            myRecentLeads: [],
            // Add Lead phone-first modal
            showPhoneModal: false,
            phoneModalInput: "",
            phoneModalError: "",
            phoneModalResults: null,
            phoneModalResultsDoneLeads: null,
            phoneModalResultsDeletedLeads: null,
            isLastLeadPostedUnder24Hours: false,
            todayCount: 0,
            thisWeekCount: 0,
            thisMonthCount: 0,
            lastMonthCount: 0,
            // Target progress
            targetProgress: null,
            // Duplicate-customer guard shown before opening a lead
            guardModal: {
                open: false,
                loading: false,
                leadId: null,
                data: null,
                error: "",
            },
        };
    }

    componentDidMount() {
        this.fetchLeads();
        this.loadSourceOptions();
        this.loadExecutives();
        this.loadTeams();
        this.loadCallStatusLabels();
        this.fetchMyRecentLeads();
        this.fetchTargetProgress();
        if (this.state.source) {
            this.loadCampaignOptions(this.state.source);
        }
    }

    // Save filters to localStorage
    saveFilters = () => {
        const { leadId, keyword, from, to, source, phone, workedBy, callStatus, campaign, team, page } = this.state;
        localStorage.setItem(FILTERS_KEY, JSON.stringify({ leadId, keyword, from, to, source, phone, workedBy, callStatus, campaign, team, page }));
    };

    // Clear filters from localStorage
    clearFilters = () => {
        localStorage.removeItem(FILTERS_KEY);
    };

    loadSourceOptions = async () => {
        try {
            const res = await API.get("/const-data");
            const data = res.data.data || res.data;
            this.setState({ sourceOptions: data.source || [] });
        } catch (e) {
            console.error("Failed to load source options", e);
        }
    };

    loadCampaignOptions = async (source) => {
        try {
            if (!source) {
                this.setState({ campaignOptions: [], campaign: "" });
                return;
            }
            const res = await API.get("/campaigns?source=" + encodeURIComponent(source));
            const data = res.data.data || res.data;
            this.setState({ campaignOptions: data || [] });
        } catch (e) {
            console.error("Failed to load campaign options", e);
        }
    };

    loadExecutives = async () => {
        try {
            const res = await API.get("/executives");
            const data = res.data.data || res.data;
            this.setState({ executiveOptions: data || [] });
        } catch (e) {
            console.error("Failed to load executives", e);
        }
    };

    loadTeams = async () => {
        // Team filter is only for holders of TELE.LEADS.VIEW_ALL_TEAMS
        if (!hasPermission(this.props.permissions, PERMISSION.LEADS_VIEW_ALL_TEAMS)) return;
        try {
            const res = await API.get("/teams");
            const data = res.data.data || res.data;
            this.setState({ teamOptions: data || [] });
        } catch (e) {
            console.error("Failed to load teams", e);
        }
    };

    loadCallStatusLabels = async () => {
        try {
            const { callStatusLabels, callStatuses } = await loadCallStatusLabels();
            this.setState({
                callStatusLabels: callStatusLabels || {},
                callStatuses: callStatuses || [],
            });
        } catch (e) {
            console.error("Failed to load call status labels", e);
        }
    };

    fetchMyRecentLeads = async () => {
        try {
            const res = await API.get("/my-recent-leads");
            const data = res.data.data || res.data;
            this.setState({
                myRecentLeads: data.leads || [],
                todayCount: data.todayCount || 0,
                thisWeekCount: data.thisWeekCount || 0,
                thisMonthCount: data.thisMonthCount || 0,
                lastMonthCount: data.lastMonthCount || 0,
            });
        } catch (e) {
            console.error("Failed to load recent leads", e);
        }
    };

    fetchTargetProgress = async () => {
        try {
            const res = await API.get("/my-target-progress");
            const data = res.data.data || res.data;
            this.setState({ targetProgress: data });
        } catch (e) {
            console.error("Failed to load target progress", e);
        }
    };

    fetchLeads = async () => {
        this.setState({ loading: true });
        try {
            const { page, pageSize, leadId, keyword, from, to, source, phone, workedBy, callStatus, campaign, team } = this.state;
            const params = {
                limit: pageSize,
                offset: page * pageSize,
            };
            if (leadId) params.lead_id = leadId;
            if (keyword) params.keyword = keyword;
            if (from) params.from = from;
            if (to) params.to = to;
            if (source) params.source = source;
            if (phone) params.phone = phone;
            if (workedBy) params.worked_by = workedBy;
            if (callStatus !== "") params.call_status = callStatus;
            if (campaign) params.campaign = campaign;
            if (team) params.team_id = team;

            const res = await API.get("/leads", { params });
            const data = res.data.data || res.data;
            this.setState({
                leads: data.leads || data || [],
                count: data.count || 0,
                loading: false,
            });
        } catch (e) {
            toast.error("Failed to load leads");
            this.setState({ loading: false });
        }
    };

    // ─── Phone-first Add Lead modal ─────────────────────────────
    handleAddLeadClick = (e) => {
        e.preventDefault();
        this.setState({
            showPhoneModal: true,
            phoneModalInput: "",
            phoneModalError: "",
            phoneModalResults: null,
            phoneModalResultsDoneLeads: null,
            phoneModalResultsDeletedLeads: null,
            isLastLeadPostedUnder24Hours: false,
        });
    };

    handlePhoneModalSearch = async () => {
        const phone = sanitizePhone(this.state.phoneModalInput);
        if (phone.length !== 10) {
            this.setState({ phoneModalError: "Please enter a valid 10 digit phone number" });
            return;
        }
        this.setState({ phoneModalSearching: true, phoneModalError: "", phoneModalInput: phone });
        try {
            const res = await API.get("/search-leads-by-phone", { params: { phone } });
            const data = res.data.data || res.data;
            const leads = data.leads || [];
            const doneLeads = data.doneLeads || [];
            const deletedLeads = data.deletedLeads || [];
            const isLastLeadPostedUnder24Hours = doneLeads.length > 0 && new Date(doneLeads[0].posted_on_raw) > new Date(Date.now() - 24 * 60 * 60 * 1000);
            this.setState({ phoneModalResults: leads, phoneModalResultsDoneLeads: doneLeads, phoneModalResultsDeletedLeads: deletedLeads, phoneModalSearching: false, isLastLeadPostedUnder24Hours });
        } catch (e) {
            toast.error("Failed to search leads");
            this.setState({ phoneModalSearching: false });
        }
    };

    closePhoneModal = () => {
        this.setState({ showPhoneModal: false, phoneModalResults: null, phoneModalResultsDoneLeads: null, phoneModalResultsDeletedLeads: null, isLastLeadPostedUnder24Hours: false });
    };

    handleSearch = (e) => {
        e.preventDefault();
        this.setState({ page: 0 }, () => {
            this.saveFilters();
            this.fetchLeads();
        });
    };

    // ─── Duplicate-customer guard modal ─────────────────────────────
    // Shown before opening a lead for editing. Opening /leads/:id/edit
    // assigns the lead on mount, so the check has to happen here first.
    openGuardModal = async (leadId) => {
        this.setState({
            guardModal: { open: true, loading: true, leadId, data: null, error: "" },
        });
        try {
            const res = await API.get(`/lead/${leadId}/related`);
            const data = res.data.data || res.data;
            this.setState((s) => ({
                guardModal: { ...s.guardModal, loading: false, data },
            }));
        } catch (e) {
            const msg =
                (e.response && e.response.data && e.response.data.message) ||
                "Failed to check other leads for this customer";
            this.setState((s) => ({
                guardModal: { ...s.guardModal, loading: false, error: msg },
            }));
        }
    };

    closeGuardModal = () => {
        this.setState({
            guardModal: { open: false, loading: false, leadId: null, data: null, error: "" },
        });
    };

    handleDelete = async (id, locationToRefresh) => {
        if (!window.confirm("Are you sure you want to delete this lead?")) return;
        try {
            let rs = await API.post(`/lead/delete/${id}`);
            if (rs.data.success !== true) {
                toast.error("Failed to delete lead");
                return;
            }
            toast.success("Lead deleted");
            if (locationToRefresh === "leadList") {
                this.fetchLeads();
            } else if (locationToRefresh === "addLead") {
                this.handlePhoneModalSearch();
                console.log("Deleted lead from addLead modal, refreshing search results");
            } else if (locationToRefresh === "guardModal") {
                // A sibling was removed - re-evaluate so the block may lift.
                this.openGuardModal(this.state.guardModal.leadId);
            } else if (locationToRefresh === "guardModalSelf") {
                // The lead being guarded was itself deleted - nothing left to open.
                this.closeGuardModal();
                this.fetchLeads();
            }

        } catch (e) {
            console.log("Failed to delete lead", e);
            const msg = (e.response && e.response.data && e.response.data.message) || "Failed to delete lead";
            toast.error(msg);
        }
    };

    handlePageChange = (newPage) => {
        this.setState({ page: newPage }, () => {
            this.saveFilters();
            this.fetchLeads();
        });
    };

    render() {
        const {
            leads,
            count,
            loading,
            page,
            pageSize,
            leadId,
            keyword,
            from,
            to,
            source,
            phone,
            workedBy,
            callStatus,
            campaign,
            team,
            sourceOptions,
            executiveOptions,
            teamOptions,
            callStatuses,
            callStatusLabels,
            myRecentLeads,
            todayCount,
            thisWeekCount,
            thisMonthCount,
            lastMonthCount,
            targetProgress,
            showPhoneModal,
            phoneModalInput,
            phoneModalError,
            phoneModalSearching,
            phoneModalResults,
            phoneModalResultsDoneLeads,
            phoneModalResultsDeletedLeads,
            isLastLeadPostedUnder24Hours,
            guardModal,
        } = this.state;
        const totalPages = Math.ceil(count / pageSize);

        const hasPendingLeads = phoneModalResults && phoneModalResults.length > 0;
        const hasDeletedLeadWithin24Hours = phoneModalResultsDeletedLeads && phoneModalResultsDeletedLeads.some(dl => new Date(dl.deleted_at_raw || dl.posted_on_raw) > new Date(Date.now() - 24 * 60 * 60 * 1000));
        let canAddLead = !hasPendingLeads && !hasDeletedLeadWithin24Hours && !isLastLeadPostedUnder24Hours;
        let canUpdateLeads = !isLastLeadPostedUnder24Hours;

        if (hasPermission(this.props.permissions, PERMISSION.LEADS_BYPASS_RESTRICTIONS)) {
            canAddLead = true;
            canUpdateLeads = true;
        }


        // Calculate target progress percentages
        const tgt = targetProgress ? targetProgress.target : {};
        const prg = targetProgress ? targetProgress.progress : {};
        const today = prg.today || {};
        const postedPct = tgt.posted ? Math.min(100, Math.round(((today.posted || 0) / tgt.posted) * 100)) : 0;
        const attemptedPct = tgt.attempted ? Math.min(100, Math.round(((today.attempted || 0) / tgt.attempted) * 100)) : 0;
        const postedPending = Math.max(0, (tgt.posted || 0) - (today.posted || 0));
        const attemptedPending = Math.max(0, (tgt.attempted || 0) - (today.attempted || 0));

        return (
            <div>
                <h2 className="page-header">
                    Leads
                </h2>



                {/* Today's count + My Last 2 Posted Leads */}
                <div className="row" style={{ marginBottom: 15 }}>
                    {targetProgress && (tgt.posted > 0 || tgt.attempted > 0) && (
                        <div className="col-md-4 col-lg-3">
                            <h4 style={{ marginTop: 0, marginBottom: 10 }}>Your Daily Target</h4>
                            {/* ── Daily Target Progress ── */}

                            <div className="row">

                                {/* <div className="col-sm-6">
                                    <div style={{ marginBottom: 10 }}>
                                        <strong>Attempted:</strong>{" "}
                                        <span style={{ fontSize: 18, fontWeight: "bold", color: attemptedPct >= 100 ? "#3c763d" : "#8a6d3b" }}>
                                            {today.attempted || 0}
                                        </span>
                                        <span style={{ color: "#999" }}> / {tgt.attempted}</span>
                                        {attemptedPct >= 100
                                            ? <span className="label label-success" style={{ marginLeft: 8 }}><i className="fas fa-check"></i> Done!</span>
                                            : <span className="label label-warning" style={{ marginLeft: 8 }}>{attemptedPending} pending</span>
                                        }
                                    </div>
                                </div> */}


                                <div className="col-xs-6" key='posted'>
                                    <div className="panel panel-info" style={{ marginBottom: 10 }}>
                                        <div className="panel-body text-center" style={{ padding: "8px 5px" }}>
                                            <span style={{ fontSize: 24, fontWeight: "bold", color: "#31708f" }}>
                                                {today.posted || 0} / {tgt.posted}
                                            </span>
                                            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#666" }}>Posted</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-xs-6" key='attempted'>
                                    <div className="panel panel-info" style={{ marginBottom: 10 }}>
                                        <div className="panel-body text-center" style={{ padding: "8px 5px" }}>
                                            <span style={{ fontSize: 24, fontWeight: "bold", color: "#31708f" }}>
                                                {today.attempted || 0} / {tgt.attempted}
                                            </span>
                                            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#666" }}>
                                                Attempted
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={`col-md-${targetProgress ? "4" : "6"} col-lg-${targetProgress ? "4" : "6"}`}>
                        <h4 style={{ marginTop: 0, marginBottom: 10 }}>Leads Posted Count</h4>
                        <div className="row">
                            {[
                                // { label: "Today", value: todayCount, color: "#31708f" },
                                { label: "This Week", value: thisWeekCount, color: "#3c763d" },
                                { label: "This Month", value: thisMonthCount, color: "#8a6d3b" },
                                { label: "Last Month", value: lastMonthCount, color: "#a94442" },
                            ].map((item) => (
                                <div className="col-xs-6 col-sm-4" key={item.label}>
                                    <div className="panel panel-info" style={{ marginBottom: 10 }}>
                                        <div className="panel-body text-center" style={{ padding: "8px 5px" }}>
                                            <span style={{ fontSize: 24, fontWeight: "bold", color: item.color }}>
                                                {item.value}
                                            </span>
                                            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#666" }}>{item.label}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={`col-md-${targetProgress ? "4" : "6"} col-lg-${targetProgress ? "5" : "6"}`}>
                        <div className="panel panel-default">
                            <div className="panel-heading">
                                <h4 style={{ margin: 0, fontSize: 14 }}>Your Last Posted Lead</h4>
                            </div>
                            <div className="panel-body" style={{ padding: 0 }}>
                                {myRecentLeads.length > 0 ? (
                                    <table className="table table-bordered table-condensed" style={{ margin: 0, fontSize: 12 }}>
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Mobile</th>
                                                <th>Service Type</th>
                                                <th>Posted On</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {myRecentLeads.map((l, i) => (
                                                <tr key={i}>
                                                    <td>{l.id}</td>
                                                    <td>{l.name}</td>
                                                    <td>{l.mobile}</td>
                                                    <td>{l.service_type}</td>
                                                    <td>{l.posted_on}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="text-muted text-center" style={{ padding: 15 }}>No recent leads</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div >
                <hr />
                <h4>Pending Leads</h4>
                {/* Filters */}
                <form onSubmit={this.handleSearch} style={{ marginBottom: 15, display: "flex", flexWrap: "wrap", gap: "6px 8px", alignItems: "center" }}>
                    <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Lead ID..."
                        value={leadId}
                        onChange={(e) => this.setState({ leadId: e.target.value.replace(/\D/g, "") })}
                        style={{ width: 100 }}
                    />
                    <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search keyword..."
                        value={keyword}
                        onChange={(e) => this.setState({ keyword: e.target.value })}
                        style={{ width: 140 }}
                    />
                    <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Phone..."
                        value={phone}
                        onChange={(e) => this.setState({ phone: sanitizePhone(e.target.value) })}
                        style={{ width: 120 }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <label style={{ margin: 0, fontSize: 12 }}>From</label>
                        <input
                            type="date"
                            className="form-control input-sm"
                            value={from}
                            onChange={(e) => this.setState({ from: e.target.value })}
                            style={{ width: 140 }}
                        />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <label style={{ margin: 0, fontSize: 12 }}>To</label>
                        <input
                            type="date"
                            className="form-control input-sm"
                            value={to}
                            onChange={(e) => this.setState({ to: e.target.value })}
                            style={{ width: 140 }}
                        />
                    </div>
                    <select
                        className="form-control input-sm"
                        value={source}
                        onChange={(e) => {
                            const newSource = e.target.value;
                            this.setState({ source: newSource, campaign: "" });
                            this.loadCampaignOptions(newSource);
                        }}
                        style={{ width: 130 }}
                    >
                        <option value="">All Sources</option>
                        {sourceOptions.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    {hasPermission(this.props.permissions, PERMISSION.LEADS_VIEW_ALL_TEAMS) && (
                        <select
                            className="form-control input-sm"
                            value={team}
                            onChange={(e) => this.setState({ team: e.target.value })}
                            style={{ width: 140 }}
                        >
                            <option value="">All Teams</option>
                            {teamOptions.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    )}
                    <select
                        className="form-control input-sm"
                        value={workedBy}
                        onChange={(e) => this.setState({ workedBy: e.target.value })}
                        style={{ width: 130 }}
                    >
                        <option value="">Worked By</option>
                        {executiveOptions.map((ex) => (
                            <option key={ex.id} value={ex.id}>{ex.name}</option>
                        ))}
                    </select>
                    <select
                        className="form-control input-sm"
                        value={callStatus}
                        onChange={(e) => this.setState({ callStatus: e.target.value })}
                        style={{ width: 150 }}
                    >
                        <option value="">Call Status</option>
                        {callStatuses.map((status) => (
                            <option key={status} value={status}>
                                {callStatusLabels[status] || status}
                            </option>
                        ))}
                    </select>
                    <select
                        className="form-control input-sm"
                        value={campaign}
                        onChange={(e) => this.setState({ campaign: e.target.value })}
                        style={{ width: 150 }}
                        disabled={!source}
                    >
                        <option value="">Campaign</option>
                        {this.state.campaignOptions.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    <button type="submit" className="btn btn-primary btn-sm">
                        <i className="fas fa-search"></i> Search
                    </button>
                    <button
                        type="button"
                        className="btn btn-default btn-sm"
                        onClick={() =>
                            this.setState({ leadId: "", keyword: "", from: "", to: "", source: "", phone: "", workedBy: "", callStatus: "", campaign: "", team: "", page: 0 }, () => {
                                this.clearFilters();
                                this.fetchLeads();
                            })
                        }
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        className="btn btn-success btn-sm"
                        style={{ marginLeft: "auto" }}
                        onClick={this.handleAddLeadClick}
                    >
                        <i className="fas fa-plus"></i> Add Lead
                    </button>
                </form>



                {/* Table */}
                {
                    loading ? (
                        <p>
                            <i className="fas fa-spinner fa-spin"></i> Loading...
                        </p>
                    ) : (
                        <div>
                            <p className="text-muted">
                                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, count)} of{" "}
                                {count} leads
                            </p>
                            <div className="table-responsive">
                                <table className="table table-striped table-bordered table-hover table-condensed" style={{ fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th>Id</th>
                                            <th>Name</th>
                                            {/* <th>Email</th> */}
                                            <th>Mobile</th>
                                            <th>Address</th>
                                            <th>Message</th>
                                            <th>Source</th>
                                            <th>Service Type</th>
                                            {/* <th>Service For</th> */}
                                            <th>Campaign</th>
                                            <th>Last Call Status</th>
                                            <th>Last Remark</th>
                                            <th>Last Assigned To</th>
                                            <th>Updated At</th>
                                            {/* <th>Status</th> */}
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leads.length === 0 ? (
                                            <tr>
                                                <td colSpan="14" className="text-center">
                                                    No leads found
                                                </td>
                                            </tr>
                                        ) : (
                                            leads.map((lead, i) => (
                                                <tr key={lead.id} style={lead.assignedToMe ? { backgroundColor: "#dff0d8", borderLeft: "4px solid #3c763d" } : {}}>
                                                    {/* <td>{page * pageSize + i + 1}</td> */}
                                                    <td>
                                                        {lead.id}
                                                        {lead.assignedToMe && (
                                                            <span className="label label-success" style={{ marginLeft: 6, fontSize: 10 }}>
                                                                <i className="fas fa-user-check"></i> Assigned to you
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{lead.name}</td>
                                                    {/* <td>{lead.email}</td> */}
                                                    <td>{lead.mobile}</td>
                                                    <td>{lead.address}</td>
                                                    <td>
                                                        <span
                                                            title={lead.message}
                                                            style={{
                                                                display: "inline-block",
                                                                maxWidth: 150,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {lead.message}
                                                        </span>
                                                    </td>
                                                    <td>{lead.source}</td>
                                                    <td>{lead.service_type}</td>
                                                    {/* <td>
                                                        {[lead.primary_service_for, lead.service_for].filter(Boolean).join(", ") || "-"}
                                                    </td> */}
                                                    <td>{lead.campaign || <span className="text-muted">-</span>}</td>
                                                    <td>
                                                        {lead.last_call_status !== undefined && lead.last_call_status !== null
                                                            ? <span className="label label-info">{getCallStatusLabel(lead.last_call_status)}</span>
                                                            : <span className="text-muted">-</span>
                                                        }
                                                    </td>
                                                    <td>
                                                        <span
                                                            title={lead.last_remark}
                                                            style={{
                                                                display: "inline-block",
                                                                maxWidth: 150,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {lead.last_remark || <span className="text-muted">-</span>}
                                                        </span>
                                                    </td>
                                                    <td>{lead.last_assigned_to || <span className="text-muted">-</span>}</td>
                                                    <td>{lead.last_updated || lead.posted_on}</td>
                                                    {/* <td>
                          <StatusBadge status={lead.status} />
                        </td> */}
                                                    <td>
                                                        <button
                                                            className="btn btn-xs btn-primary"
                                                            title="Edit"
                                                            onClick={() => this.openGuardModal(lead.id)}
                                                        >
                                                            <i className="fas fa-edit"></i>&nbsp;Update
                                                        </button>{" "}
                                                        <button
                                                            className="btn btn-xs btn-danger"
                                                            title="Delete"
                                                            onClick={() => this.handleDelete(lead.id, "leadList")}
                                                        >
                                                            <i className="fas fa-trash"></i>&nbsp;Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <nav className="text-center">
                                    <ul className="pagination">
                                        <li className={page === 0 ? "disabled" : ""}>
                                            <a
                                                href="#prev"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (page > 0) this.handlePageChange(page - 1);
                                                }}
                                            >
                                                &laquo;
                                            </a>
                                        </li>
                                        {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                                            // Show pages around current page
                                            let p;
                                            if (totalPages <= 10) {
                                                p = i;
                                            } else {
                                                const start = Math.max(0, Math.min(page - 4, totalPages - 10));
                                                p = start + i;
                                            }
                                            return (
                                                <li key={p} className={page === p ? "active" : ""}>
                                                    <a
                                                        href={`#page-${p}`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            this.handlePageChange(p);
                                                        }}
                                                    >
                                                        {p + 1}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                        <li className={page >= totalPages - 1 ? "disabled" : ""}>
                                            <a
                                                href="#next"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (page < totalPages - 1) this.handlePageChange(page + 1);
                                                }}
                                            >
                                                &raquo;
                                            </a>
                                        </li>
                                    </ul>
                                </nav>
                            )}
                        </div>
                    )
                }

                {/* Phone-first Add Lead Modal */}
                {showPhoneModal && (
                    <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
                        <div className="modal-dialog modal-lg" style={{ marginTop: 50 }}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <button type="button" className="close" onClick={this.closePhoneModal}>&times;</button>
                                    <h4 className="modal-title">
                                        <i className="fas fa-phone"></i> Add Lead — Enter Phone Number
                                    </h4>
                                </div>
                                <div className="modal-body" style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>
                                    {/* Phone input */}
                                    <div className="form-group">
                                        <label>Phone Number *</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className={`form-control${phoneModalError ? " input-error" : ""}`}
                                                placeholder="10 digit phone number"
                                                value={phoneModalInput}
                                                onChange={(e) => this.setState({
                                                    phoneModalInput: sanitizePhone(e.target.value),
                                                    phoneModalError: "",
                                                })}
                                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); this.handlePhoneModalSearch(); } }}
                                                autoFocus
                                            />
                                            <span className="input-group-btn">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={this.handlePhoneModalSearch}
                                                    disabled={phoneModalSearching}
                                                >
                                                    {phoneModalSearching
                                                        ? <span><i className="fas fa-spinner fa-spin"></i> Searching...</span>
                                                        : <span><i className="fas fa-search"></i> Search</span>
                                                    }
                                                </button>
                                            </span>
                                        </div>
                                        {phoneModalError && (
                                            <span className="text-danger" style={{ fontSize: 12 }}>
                                                <i className="fas fa-exclamation-circle"></i> {phoneModalError}
                                            </span>
                                        )}
                                    </div>

                                    {/* Search results */}
                                    {(phoneModalResults !== null || phoneModalResultsDoneLeads !== null || phoneModalResultsDeletedLeads !== null) && (
                                        <div>

                                            {phoneModalResultsDeletedLeads && phoneModalResultsDeletedLeads.length > 0 && (
                                                <div style={{ marginBottom: 20 }}>
                                                    <div className="alert alert-danger" style={{ marginBottom: 10 }}>
                                                        <i className="fas fa-trash"></i>{" "}
                                                        <strong>Deleted Leads (Last 2 Days)</strong>
                                                    </div>
                                                    <table className="table table-bordered table-striped" style={{ fontSize: 12 }}>
                                                        <thead>
                                                            <tr>
                                                                <th>Lead ID</th>
                                                                <th>Service Type</th>
                                                                <th>Call Status</th>
                                                                <th>Deleted By</th>
                                                                <th>Deleted On</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {phoneModalResultsDeletedLeads.map((lead, i) => (
                                                                <tr key={i}>
                                                                    <td>{lead.id}</td>
                                                                    <td>{lead.service_type || "-"}</td>
                                                                    <td>{lead.call_status || "-"}</td>
                                                                    <td>{lead.deleted_by || "-"}</td>
                                                                    <td>{lead.deleted_at || lead.posted_on}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            {phoneModalResultsDoneLeads && phoneModalResultsDoneLeads.length > 0 && (
                                                <div>
                                                    <div className="alert alert-success" style={{ marginBottom: 10 }}>
                                                        <i className="fas fa-info-circle"></i>{" "}
                                                        <strong>Posted Leads</strong>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-condensed" style={{ fontSize: 12 }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Lead ID</th>
                                                                    <th>Service Type</th>
                                                                    <th>Posted On</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {phoneModalResultsDoneLeads.map((lead, i) => (
                                                                    <tr key={i}>
                                                                        <td>{lead.id}</td>
                                                                        <td>{lead.service_type || "-"}</td>
                                                                        <td>{lead.posted_on}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <hr style={{ margin: "10px 0" }} />
                                                </div>
                                            )}

                                            {phoneModalResults && phoneModalResults.length > 0 ? (
                                                <div>
                                                    <div className="alert alert-warning" style={{ marginBottom: 10 }}>
                                                        <i className="fas fa-exclamation-triangle"></i>{" "}
                                                        <strong> {phoneModalResults.length} Pending Leads</strong>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-condensed" style={{ fontSize: 12 }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Lead ID</th>
                                                                    <th>Service Type</th>
                                                                    <th>Last Call Status</th>
                                                                    <th>Last Assigned To</th>
                                                                    <th>Last Updated At</th>
                                                                    <th>Created On</th>
                                                                    <th>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {phoneModalResults.map((lead, i) => (
                                                                    <tr key={i}>
                                                                        <td>{lead.lead_id}</td>
                                                                        <td>{lead.service_type || "-"}</td>
                                                                        <td>
                                                                            {lead.last_call_status !== undefined && lead.last_call_status !== null
                                                                                ? <span className="label label-info">{getCallStatusLabel(lead.last_call_status)}</span>
                                                                                : "-"
                                                                            }
                                                                        </td>
                                                                        <td>{lead.last_assigned_to || "-"}</td>
                                                                        <td>{lead.updated_at || "-"}</td>
                                                                        <td>{lead.posted_on}</td>
                                                                        <td>
                                                                            {canUpdateLeads && (

                                                                                <>
                                                                                    <Link
                                                                                        to={`/leads/${lead.lead_id}/edit`}
                                                                                        className="btn btn-xs btn-primary"
                                                                                        onClick={this.closePhoneModal}
                                                                                    >
                                                                                        <i className="fas fa-edit"></i> Update
                                                                                    </Link> &nbsp;
                                                                                </>
                                                                            )}
                                                                            <button
                                                                                className="btn btn-xs btn-danger"
                                                                                onClick={() => this.handleDelete(lead.lead_id, "addLead")}
                                                                            >
                                                                                <i className="fas fa-trash"></i> Delete
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {canAddLead && (
                                                        <>
                                                            <hr style={{ margin: "10px 0" }} />
                                                            <p className="text-muted" style={{ fontSize: 12 }}>
                                                                If none of the above leads match, you can still add a new lead:
                                                            </p>
                                                            <Link
                                                                to={`/leads/add?mobile=${phoneModalInput}`}
                                                                className="btn btn-warning btn-sm"
                                                                onClick={this.closePhoneModal}
                                                            >
                                                                <i className="fas fa-plus"></i> Add New Lead Anyway
                                                            </Link>
                                                        </>)}
                                                </div>
                                            ) : canAddLead && (
                                                <div>
                                                    <div className="alert alert-success">
                                                        <i className="fas fa-check-circle"></i> No existing leads found for this phone number.
                                                    </div>
                                                    <Link
                                                        to={`/leads/add?mobile=${phoneModalInput}`}
                                                        className="btn btn-success"
                                                        onClick={this.closePhoneModal}
                                                    >
                                                        <i className="fas fa-plus"></i> Add New Lead
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-default" onClick={this.closePhoneModal}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Duplicate-customer guard modal (before opening a lead) */}
                {guardModal.open && (
                    <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
                        <div className="modal-dialog modal-lg" style={{ marginTop: 50 }}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <button type="button" className="close" onClick={this.closeGuardModal}>&times;</button>
                                    <h4 className="modal-title">
                                        <i className="fas fa-users"></i> Other Leads For This Customer
                                        {guardModal.leadId && <span className="text-muted" style={{ fontSize: 13 }}> — opening Lead #{guardModal.leadId}</span>}
                                    </h4>
                                </div>
                                <div className="modal-body" style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>
                                    {guardModal.loading && (
                                        <p><i className="fas fa-spinner fa-spin"></i> Checking other leads for this customer...</p>
                                    )}

                                    {!guardModal.loading && guardModal.error && (
                                        <div className="alert alert-danger">
                                            <i className="fas fa-exclamation-circle"></i> {guardModal.error}
                                        </div>
                                    )}

                                    {!guardModal.loading && guardModal.data && (
                                        <div>
                                            {/* Why the user is blocked */}
                                            {guardModal.data.blockedReasons.length > 0 && (
                                                <div className={`alert ${guardModal.data.canProceed ? "alert-info" : "alert-danger"}`}>
                                                    <strong>
                                                        <i className="fas fa-exclamation-triangle"></i>{" "}
                                                        {guardModal.data.canProceed
                                                            ? "Restrictions bypassed by your permissions:"
                                                            : "You cannot work this lead yet:"}
                                                    </strong>
                                                    <ul style={{ marginBottom: 0, marginTop: 6 }}>
                                                        {guardModal.data.blockedReasons.map((r) => (
                                                            <li key={r.code}>{r.message}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {guardModal.data.blockedReasons.length === 0 && (
                                                <div className="alert alert-success">
                                                    <i className="fas fa-check-circle"></i> No other pending leads for this customer. You can proceed.
                                                </div>
                                            )}

                                            {/* Other pending leads — must be cleared first */}
                                            {guardModal.data.pendingLeads.length > 0 && (
                                                <div style={{ marginBottom: 20 }}>
                                                    <div className="alert alert-warning" style={{ marginBottom: 10 }}>
                                                        <i className="fas fa-exclamation-triangle"></i>{" "}
                                                        <strong>{guardModal.data.pendingLeads.length} Other Pending Lead(s)</strong>
                                                        {" — "}delete the ones that are not relevant.
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-condensed" style={{ fontSize: 12 }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Lead ID</th>
                                                                    <th>Service Type</th>
                                                                    <th>Last Call Status</th>
                                                                    <th>Last Assigned To</th>
                                                                    <th>Currently Held By</th>
                                                                    <th>Created On</th>
                                                                    <th>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {guardModal.data.pendingLeads.map((lead) => (
                                                                    <tr key={lead.lead_id}>
                                                                        <td>{lead.lead_id}</td>
                                                                        <td>{lead.service_type || "-"}</td>
                                                                        <td>
                                                                            {lead.last_call_status !== undefined && lead.last_call_status !== null
                                                                                ? <span className="label label-info">{getCallStatusLabel(lead.last_call_status)}</span>
                                                                                : "-"
                                                                            }
                                                                        </td>
                                                                        <td>{lead.last_assigned_to || "-"}</td>
                                                                        <td>
                                                                            {lead.held_by_user_id
                                                                                ? <span className={`label ${lead.heldByMe ? "label-success" : "label-danger"}`}>
                                                                                    {lead.heldByMe ? "You" : lead.held_by_name || "Another user"}
                                                                                </span>
                                                                                : <span className="text-muted">-</span>
                                                                            }
                                                                        </td>
                                                                        <td>{lead.posted_on}</td>
                                                                        <td>
                                                                            {lead.canDelete ? (
                                                                                <button
                                                                                    className="btn btn-xs btn-danger"
                                                                                    onClick={() => this.handleDelete(lead.lead_id, "guardModal")}
                                                                                >
                                                                                    <i className="fas fa-trash"></i> Delete
                                                                                </button>
                                                                            ) : (
                                                                                <span className="text-muted" style={{ fontSize: 11 }}>
                                                                                    <i className="fas fa-lock"></i> In progress — cannot delete
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Posted leads */}
                                            {guardModal.data.doneLeads.length > 0 && (
                                                <div style={{ marginBottom: 20 }}>
                                                    <div className="alert alert-success" style={{ marginBottom: 10 }}>
                                                        <i className="fas fa-info-circle"></i> <strong>Posted Leads</strong>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-condensed" style={{ fontSize: 12 }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Lead ID</th>
                                                                    <th>Service Type</th>
                                                                    <th>Posted On</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {guardModal.data.doneLeads.map((lead) => (
                                                                    <tr key={lead.id}>
                                                                        <td>{lead.id}</td>
                                                                        <td>{lead.service_type || "-"}</td>
                                                                        <td>
                                                                            {lead.posted_on}
                                                                            {lead.within_24h && (
                                                                                <span className="label label-danger" style={{ marginLeft: 6 }}>within 24h</span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Assignment history for this lead */}
                                            {guardModal.data.assignmentHistory && guardModal.data.assignmentHistory.length > 0 && (
                                                <div style={{ marginBottom: 20 }}>
                                                    <div className="alert alert-info" style={{ marginBottom: 10 }}>
                                                        <i className="fas fa-history"></i> <strong>Assignment History</strong>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-condensed" style={{ fontSize: 12 }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Date/Time</th>
                                                                    <th>Assignee</th>
                                                                    <th>Status</th>
                                                                    <th>Remark</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {guardModal.data.assignmentHistory.map((h, i) => (
                                                                    <tr key={i}>
                                                                        <td>{h.updated_at}</td>
                                                                        <td>{h.assigned_to || "-"}</td>
                                                                        <td>
                                                                            {h.call_status !== undefined && h.call_status !== null
                                                                                ? <span className="label label-info">{h.call_status}</span>
                                                                                : "-"
                                                                            }
                                                                        </td>
                                                                        <td>{h.remark || "-"}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Deleted leads (context only) */}
                                            {guardModal.data.deletedLeads.length > 0 && (
                                                <div>
                                                    <div className="alert alert-danger" style={{ marginBottom: 10 }}>
                                                        <i className="fas fa-trash"></i> <strong>Deleted Leads (Last 2 Days)</strong>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-bordered table-condensed" style={{ fontSize: 12 }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Lead ID</th>
                                                                    <th>Service Type</th>
                                                                    <th>Call Status</th>
                                                                    <th>Deleted By</th>
                                                                    <th>Deleted On</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {guardModal.data.deletedLeads.map((lead) => (
                                                                    <tr key={lead.id}>
                                                                        <td>{lead.id}</td>
                                                                        <td>{lead.service_type || "-"}</td>
                                                                        <td>{lead.call_status || "-"}</td>
                                                                        <td>{lead.deleted_by || "-"}</td>
                                                                        <td>{lead.deleted_at || lead.posted_on}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    {guardModal.data && (
                                        <button
                                            type="button"
                                            className="btn btn-danger pull-left"
                                            onClick={() => this.handleDelete(guardModal.leadId, "guardModalSelf")}
                                        >
                                            <i className="fas fa-trash"></i> Delete This Lead
                                        </button>
                                    )}
                                    {guardModal.data && guardModal.data.canProceed && (
                                        <Link
                                            to={`/leads/${guardModal.leadId}/edit`}
                                            className="btn btn-primary"
                                            onClick={this.closeGuardModal}
                                        >
                                            <i className="fas fa-edit"></i> Proceed to Update
                                        </Link>
                                    )}
                                    <button type="button" className="btn btn-default" onClick={this.closeGuardModal}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div >
        );
    }
}

const StatusBadge = ({ status }) => {
    const info = STATUS_MAP[status] || { label: status, cls: "label-default" };
    return <span className={`label ${info.cls}`}>{info.label}</span>;
};

const mapStateToProps = (state) => ({ permissions: state.permissions });

export default connect(mapStateToProps)(LeadsPage);
